/** AssistantPane header section and conversation switcher. The close button renders only when an onClose handler is supplied (the chats SPA tab is always visible, so it passes none). */

import { colors } from '@federa/components/css-refs.colors';
import { typography } from '@federa/components/css-refs.typography';
import { spacing } from '@federa/components/css-refs.spacing';
import { motion, borderRadius } from '@federa/components/css-refs.effects';
import { SemanticIcon } from '@federa/components/icons/SemanticIcon';
import { Button } from '@federa/components/primitives/Button';
import { CloseButton } from '@federa/components/primitives/CloseButton';
import { HEADER_BAR_BASE_STYLE, MODAL_HEADER_ROW_CLASS, LAYOUT_CLASSES } from '@federa/components/common/layout-styles';
import { TEXT_TRUNCATE_STYLE } from '@federa/const/style-text-presets';
import { FULL_WIDTH_STYLE } from '@federa/const/style-size-presets';
import { FLEX_ROW_CENTER_GAP2_STYLE } from '@federa/components/common/style-primitives';
import { ConversationListDropdown } from './AssistantPane.components.conversation.tsx';
import type { CSSProperties } from 'react';
import type { ChatRoomStruct } from '@federa/types/conversations-chat-structs';

/** The header-local delta over a ghost `Button`: the shallow header-height box and the title's own type. Fill, hover
 *  repaint and cursor are the variant's -- this button used to paint its own hover by writing `currentTarget.style`,
 *  a second styling channel React could not see. */
const SWITCHER_BUTTON: CSSProperties = {
	paddingLeft: spacing[2],
	paddingRight: spacing[2],
	paddingTop: spacing[1],
	paddingBottom: spacing[1],
	borderRadius: borderRadius.lg,
	color: colors.text.primary,
	fontSize: typography.fontSize.base,
	fontWeight: typography.semanticWeights.action,
};
/** `Button` wraps its children in a single span, so the switcher's two-part layout -- growing title, trailing chevron
 *  -- lives on that span rather than on the button, which sees only one child. */
const SWITCHER_ROW: CSSProperties = {
	...FLEX_ROW_CENTER_GAP2_STYLE,
	...FULL_WIDTH_STYLE,
	minWidth: 0,
};
const SWITCHER_TITLE: CSSProperties = {
	...TEXT_TRUNCATE_STYLE,
	flex: 1,
	textAlign: 'left',
};
const SWITCHER_CHEVRON_BASE: CSSProperties = { transition: motion.duration.medium };
const TITLE_SECTION_GAP: CSSProperties = { gap: spacing[3] };
const STATIC_ASSISTANT_TITLE: CSSProperties = {
	gap: spacing[2],
	color: colors.text.primary,
	fontSize: typography.fontSize.base,
	fontWeight: typography.semanticWeights.action,
};
const HEADER_BAR_STYLE: CSSProperties = {
	...HEADER_BAR_BASE_STYLE,
	borderColor: colors.border.muted,
	// Same reasoning as the composer's `flexShrink: 0`: fixed chrome in a flex column whose scrolling child has a zero
	// flex-basis absorbs the negative free space instead. Set locally, since HEADER_BAR_BASE_STYLE has consumers elsewhere.
	flexShrink: 0,
};
const HEADER_ACTIONS_GROUP: CSSProperties = { gap: spacing[2] };
/** The header-local delta over `CloseButton`: the rounded-rect corner the header chrome uses rather than the
 *  primitive's circle, and secondary ink. The box itself is the primitive's fixed 28px target. */
const HEADER_CLOSE_BUTTON: CSSProperties = {
	borderRadius: borderRadius.lg,
	color: colors.text.secondary,
};

function buildSwitcherChevronStyle(expanded: boolean): CSSProperties {
	return {
		...SWITCHER_CHEVRON_BASE,
		transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
	};
}

/** Conversation switcher button for G4 users */
interface ConversationSwitcherProps {
	title: string;
	expanded: boolean;
	onToggle: () => void;
}

function ConversationSwitcher({ title, expanded, onToggle }: ConversationSwitcherProps) {
	return (
		<Button
			variant="ghost"
			size="md"
			onClick={onToggle}
			className="min-w-0 flex-1"
			aria-expanded={expanded}
			aria-haspopup="listbox"
			style={SWITCHER_BUTTON}
			title={title}
		>
			<span style={SWITCHER_ROW}>
				<span style={SWITCHER_TITLE}>{title}</span>
				<span style={buildSwitcherChevronStyle(expanded)}>
					<SemanticIcon name="expand" size="xs" color="primary" />
				</span>
			</span>
		</Button>
	);
}

interface HeaderTitleProps {
	canSwitchConversations: boolean;
	conversationTitle: string;
	showConversationList: boolean;
	setShowConversationList: (show: boolean) => void;
}

function HeaderTitleSection({
	canSwitchConversations,
	conversationTitle,
	showConversationList,
	setShowConversationList,
}: HeaderTitleProps) {
	return (
		<div className="flex items-center flex-1 min-w-0" style={TITLE_SECTION_GAP}>
			{canSwitchConversations ? (
				<ConversationSwitcher
					title={conversationTitle}
					expanded={showConversationList}
					onToggle={() => setShowConversationList(!showConversationList)}
				/>
			) : (
				<div className={LAYOUT_CLASSES.row} style={STATIC_ASSISTANT_TITLE}>
					<span style={TEXT_TRUNCATE_STYLE}>{conversationTitle}</span>
				</div>
			)}
		</div>
	);
}

/**
 * Header section; the close button renders only when onClose is provided.
 *
 * THE DROPDOWN RENDERS HERE, not at the pane, and that is the whole reason this component takes the conversation list.
 * `showConversationList` is the switcher button's own expanded state -- it is written by the button in this file and
 * read by the panel the button opens, so a parent mediating between the two made one control's open/closed state a
 * property of the pane. The rendered DOM is unchanged: the panel is still the header bar's next sibling.
 */
export function HeaderSection({
	canSwitchConversations,
	conversationTitle,
	showConversationList,
	setShowConversationList,
	conversations,
	currentConversationId,
	onSelectConversation,
	onClose,
}: HeaderTitleProps & {
	conversations: ChatRoomStruct[];
	currentConversationId: string | undefined;
	onSelectConversation: (room: ChatRoomStruct) => void;
	onClose?: () => void;
}) {
	return (
		<>
			<div className={MODAL_HEADER_ROW_CLASS} style={HEADER_BAR_STYLE}>
				<HeaderTitleSection
					canSwitchConversations={canSwitchConversations}
					conversationTitle={conversationTitle}
					showConversationList={showConversationList}
					setShowConversationList={setShowConversationList}
				/>
				{onClose && (
					<div className={LAYOUT_CLASSES.row} style={HEADER_ACTIONS_GROUP}>
						<CloseButton size="sm" onClick={onClose} style={HEADER_CLOSE_BUTTON} aria-label="Close Assistant" />
					</div>
				)}
			</div>
			{canSwitchConversations && showConversationList && (
				<ConversationListDropdown
					conversations={conversations}
					currentConversationId={currentConversationId}
					onSelectConversation={onSelectConversation}
				/>
			)}
		</>
	);
}
